import React, { useState, useCallback } from 'react';
import { AdminConsoleHeader } from './AdminConsoleHeader';
import { AdminConsoleTabs } from './AdminConsoleTabs';
import { type AdminConsoleTabId } from './types';
import './AdminConsole.css';
import { LayersTab } from './tabs/LayersTab';
import { SecurityGroupsTab } from './tabs/SecurityGroupsTab';
import { UsersTab } from './tabs/UsersTab';
import { CardTypesTab } from './tabs/CardTypesTab';
import { CustomDetailsTab } from './tabs/CustomDetailsTab';
import { AuthenticationTab } from './tabs/AuthenticationTab';
import { LinkToCustomDetailsTab } from './tabs/LinkToCustomDetailsTab';
import { CustomBrandingTab } from './tabs/CustomBrandingTab';
import { SubscriptionTab } from './tabs/SubscriptionTab';
import { PositionsTab } from './tabs/PositionsTab';
import { CompanyConfigurationsTab } from './tabs/CompanyConfigurationsTab';
import { SubmitTimeConfigTab } from './tabs/SubmitTimeConfigTab';
import { PhasesManagerTab } from './tabs/PhasesManagerTab';
import { ReportConfigTab } from './tabs/ReportConfigTab';
import adminConsoleService from '@services/adminConsoleService';

const IS_BETA_KEY = 'C2_isBeta';
export const IS_BETA_EVENT = 'C2_isBeta_changed';

const TAB_CONTENT: Record<AdminConsoleTabId, React.ReactNode> = {
  layers: <LayersTab />,
  'security-groups': <SecurityGroupsTab />,
  users: <UsersTab />,
  'card-types': <CardTypesTab />,
  'custom-details': <CustomDetailsTab prevOverflow={false} />,
  authentication: <AuthenticationTab />,
  'link-to-custom-details': <LinkToCustomDetailsTab />,
  'custom-branding': <CustomBrandingTab />,
  subscription: <SubscriptionTab />,
  positions: <PositionsTab />,
  'company-configurations': <CompanyConfigurationsTab />,
  'submit-time-configuration': <SubmitTimeConfigTab />,
  'phases-manager': <PhasesManagerTab />,
  'report-configuration': <ReportConfigTab />,
};

export const AdminConsole: React.FC = () =>
{
  const [activeTab, setActiveTab] = useState<AdminConsoleTabId>('layers');
  const [isBeta, setIsBeta] = useState(() => localStorage.getItem(IS_BETA_KEY) === 'true');

  const handleChangeBeta = useCallback(async (value: boolean) =>
  {
    setIsBeta(value);
    localStorage.setItem(IS_BETA_KEY, String(value));
    window.dispatchEvent(new CustomEvent(IS_BETA_EVENT, { detail: value }));
    try
    {
      await adminConsoleService.updateBeta(value);
    }
    catch
    {
      // Rollback on failure
      setIsBeta(!value);
      localStorage.setItem(IS_BETA_KEY, String(!value));
      window.dispatchEvent(new CustomEvent(IS_BETA_EVENT, { detail: !value }));
    }
  }, []);

  return (
    <div className="admin-console h-full flex flex-col bg-white dark:bg-[#2d2f2f]">
      <AdminConsoleHeader isBeta={isBeta} showBetaButton={true} onChangeBeta={handleChangeBeta} />

      <AdminConsoleTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="flex-1 overflow-y-auto bg-[#f7f7f7]">{TAB_CONTENT[activeTab]}</div>
    </div>
  );
};
